import { ChevronRight, Code2, ArrowLeftRight, ShieldCheck, Lock } from "lucide-react";
import type { EngageService } from "@/hooks/useEngageServices";
import type { EngageServiceKey } from "./engageServiceCopy";

const SERVICE_ICONS: Record<EngageServiceKey, typeof Code2> = {
  development: Code2,
  migration: ArrowLeftRight,
  production: ShieldCheck,
};

interface EngageServiceCardsProps {
  services: EngageService[];
  onPick: (service: EngageServiceKey) => void;
  /** Hidden when the caller already explains the offer above the cards. */
  showHelpNote?: boolean;
}

/**
 * The three support services as selectable cards. Shared so the account-level entry point
 * and the in-project chooser present an identical offer, whichever order they run in.
 */
export default function EngageServiceCards({
  services, onPick, showHelpNote = true,
}: EngageServiceCardsProps) {
  return (
    <div className="space-y-2.5">
      {services.map((svc) => {
        const Icon = SERVICE_ICONS[svc.key];
        return (
          <button
            key={svc.key}
            type="button"
            disabled={!svc.enabled}
            onClick={() => onPick(svc.key)}
            className={`group w-full flex items-start gap-4 text-left rounded-2xl border p-4 transition-all duration-200 ${
              svc.enabled
                ? "border-line bg-white hover:border-plum/40 hover:bg-selected  hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/5 dark:hover:border-violet-700/50 dark:hover:bg-violet-950/20"
                : "border-line bg-soft cursor-not-allowed dark:border-white/10 dark:bg-white/5"
            }`}
          >
            <span className={`w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors ${
              svc.enabled
                ? "bg-gradient-to-br from-soft-lavender-100 to-sunset-coral/10 group-hover:from-soft-lavender-200 group-hover:to-sunset-coral/20 dark:from-violet-950/40 dark:to-rose-950/20 dark:group-hover:from-violet-950/60 dark:group-hover:to-rose-950/30"
                : "bg-soft"
            }`}>
              {svc.enabled
                ? <Icon className="w-5 h-5 text-plum" />
                : <Lock className="w-4 h-4 text-muted-foreground" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className={`block text-sm font-bold ${svc.enabled ? "text-foreground" : "text-muted-foreground"}`}>
                {svc.title}
              </span>
              <span className={`block text-xs mt-1 leading-relaxed ${svc.enabled ? "text-muted-foreground" : "text-muted-foreground"}`}>
                {svc.blurb}
              </span>
              <span className="flex flex-wrap gap-1.5 mt-2.5">
                {svc.badges.map((b) => (
                  <span
                    key={b.text}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      b.tone === "positive"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700 font-semibold dark:bg-emerald-950/30 dark:border-emerald-900/40 dark:text-emerald-300"
                        : "bg-soft border-line text-muted-foreground dark:bg-white/5 dark:border-white/10 dark:text-gray-400"
                    }`}
                  >
                    {b.text}
                  </span>
                ))}
              </span>
              {!svc.enabled && svc.disabledReason && (
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-2">
                  <Lock className="w-3 h-3 flex-shrink-0" />{svc.disabledReason}
                </span>
              )}
            </span>
            {svc.enabled && (
              <ChevronRight className="w-4 h-4 text-muted-foreground self-center flex-shrink-0 transition-all group-hover:text-sunset-coral dark:group-hover:text-rose-400 group-hover:translate-x-0.5" />
            )}
          </button>
        );
      })}

      {showHelpNote && (
        <div className="rounded-xl border border-line bg-selected p-4 dark:border-violet-900/40 dark:bg-violet-950/30">
          <div className="text-sm font-semibold text-plum">Not sure which one fits?</div>
          <div className="text-xs text-plum mt-1 leading-relaxed">
            Pick development support and describe what is going on. We will tell you which of
            these you actually need before anything is committed.
          </div>
        </div>
      )}
    </div>
  );
}
